import * as React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { ConfigurationModel, GroupModel } from 'franke-trough-dependencies';
import classNames from 'classnames';

import { GlobalPrivateState, QueryParameterState } from 'src/api';
import { FolderListItem } from './ListItem/FolderListItem';
import { RouterState } from 'connected-react-router';

import './FolderList.scss';
import { Icon } from '../../Images';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

import { FormattedMessage, FormattedDate } from 'react-intl';
import MESSAGES from './FolderList.messages';
import { requestAPIActions, RequestApiHttpPayload } from 'loopback-react-request';
import { isToday, isThisMonth, format } from 'date-fns';
import { isThisWeek } from 'date-fns/esm';

export interface FolderListHeaderedProps {
  authenticator: GlobalPrivateState['authenticator'];
  user: GlobalPrivateState['user'];
  groups: GlobalPrivateState['groups'];
  userData: GlobalPrivateState['userData'];
  location: RouterState['location'];
  params: QueryParameterState['params'];
  queryState: GlobalPrivateState['queryState'];
  limit?: number;
  folderId?: string;
  dispatch: Function;
}

export interface FolderListHeaderedState {
  showSortSettings: boolean;
  sortBy: string;
}

const groupFoldersAlphabetically = (folders: GroupModel[]) => {
  const alphabetGroups: Record<string, GroupModel[]> = {};

  folders.forEach((folder) => {
    const firstLetter = folder.name.charAt(0).toUpperCase();
    
    if (!alphabetGroups[firstLetter]) {
      alphabetGroups[firstLetter] = [];
    }

    alphabetGroups[firstLetter].push(folder);
  });

  return alphabetGroups;
};

const groupFoldersByDate = (folders: GroupModel[]) => {
  const today: GroupModel[] = [];
  const last7Days: GroupModel[] = [];
  const last30Days: GroupModel[] = [];
  const older: any = {};

  folders.forEach((folder) => {
    const updatedAt = new Date(folder.updated_at);

    if (isToday(updatedAt)) {
      today.push(folder);
    } else if (isThisWeek(updatedAt)) {
      last7Days.push(folder);
    } else if (isThisMonth(updatedAt)) {
      last30Days.push(folder);
    } else {
      const monthYear = {
        month: updatedAt.getMonth() + 1,
        year: updatedAt.getFullYear(),
      };
      const key = `${monthYear.month}-${monthYear.year}`;

      if (!older[key]) {
        older[key] = {
          monthYear,
          folders: [],
        };
      }
      older[key].folders.push(folder);
    }
  });

  return { today, last7Days, last30Days, older };
};

export class FolderListHeaderedPartial extends React.Component<FolderListHeaderedProps, FolderListHeaderedState> {
  private toggleRef: React.RefObject<HTMLDivElement>;

  constructor(props: FolderListHeaderedProps) {
    super(props);
    this.toggleRef = React.createRef();
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.state = {
      showSortSettings: false,
      sortBy: 'date',
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOutsideClick);
  }

  componentDidUpdate(prevProps: FolderListHeaderedProps) {
    const prevGroups = prevProps.groups && prevProps.groups.result;
    const groups = this.props.groups && this.props.groups.result;

    const foldersChanged = JSON.stringify(prevGroups) !== JSON.stringify(groups);

    if (foldersChanged && groups) {
      const newState = this.state;
      this.setState(newState);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
  }

  private handleOutsideClick(event: MouseEvent) {
    if (this.toggleRef && this.toggleRef.current && !this.toggleRef.current.contains(event.target as Node)) {
      this.setState({ showSortSettings: false });
    }
  }

  handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({ sortBy: event.target.value });
  };

  public render() {
    const { groups, user, userData, params } = this.props;
    const { showSortSettings, sortBy } = this.state;
    const isGroupsReady = this.groupsForCurrentTab && Array.isArray(this.groupsForCurrentTab) && this.groupsForCurrentTab.length > 0;
    
    let groupedFolders: Record<string, GroupModel[]>;

    if (sortBy === 'name') {
      groupedFolders = groupFoldersAlphabetically(this.groupsForCurrentTab || []);
    } else {
      groupedFolders = groupFoldersByDate(this.groupsForCurrentTab || []);
    }

    return (
      <div className="partial-folder-list w-100 d-flex flex-wrap">
        <div className="folder-list-header w-100 d-flex justify-content-between align-items-center">
          <h5 className="heading heading--all">
            <FormattedMessage {...MESSAGES.folderListHeadingAll} /> ({this.groupsForCurrentTab && this.groupsForCurrentTab.length || 0})
          </h5>
          <div className={['dropdown-wrapper', showSortSettings ? 'active' : 'inactive'].join(' ')} ref={this.toggleRef}>
            <div className="dropdown-toggle" onClick={() => {this.setState({showSortSettings: !showSortSettings});}}>
              {sortBy === 'date' ? 
                <FormattedMessage {...MESSAGES.folderListSortByDate} /> : 
                <FormattedMessage {...MESSAGES.folderListSortByName} />
              }
              <svg width="12" height="8" viewBox="0 0 12 8" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.9997 1.17006C10.8123 0.983814 10.5589 0.879272 10.2947 0.879272C10.0305 0.879272 9.77707 0.983814 9.5897 1.17006L5.9997 4.71006L2.4597 1.17006C2.27234 0.983814 2.01889 0.879272 1.7547 0.879272C1.49052 0.879272 1.23707 0.983814 1.0497 1.17006C0.955976 1.26303 0.881582 1.37363 0.830813 1.49549C0.780044 1.61735 0.753906 1.74805 0.753906 1.88006C0.753906 2.01208 0.780044 2.14278 0.830813 2.26464C0.881582 2.3865 0.955976 2.4971 1.0497 2.59007L5.2897 6.83006C5.38267 6.92379 5.49327 6.99819 5.61513 7.04896C5.73699 7.09972 5.86769 7.12586 5.9997 7.12586C6.13172 7.12586 6.26242 7.09972 6.38428 7.04896C6.50614 6.99819 6.61674 6.92379 6.7097 6.83006L10.9997 2.59007C11.0934 2.4971 11.1678 2.3865 11.2186 2.26464C11.2694 2.14278 11.2955 2.01208 11.2955 1.88006C11.2955 1.74805 11.2694 1.61735 11.2186 1.49549C11.1678 1.37363 11.0934 1.26303 10.9997 1.17006Z" />
              </svg>
            </div>
            {showSortSettings && (
              <div className="dropdown-list">
                <ul>
                  <li className={sortBy === 'date' ? 'disabled' : ''} onClick={() => {
                    this.setState({
                      showSortSettings: false,
                      sortBy: 'date'
                    });
                  }}>
                    <FormattedMessage {...MESSAGES.folderListSortByDate} />
                  </li>
                  <li className={sortBy === 'name' ? 'disabled' : ''} onClick={() => {
                    this.setState({
                      showSortSettings: false,
                      sortBy: 'name'
                    });
                  }}>
                    <FormattedMessage {...MESSAGES.folderListSortByName} />
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className={classNames('folder-view', 'w-100')}>
          <div className="row">
            {isGroupsReady && (
              <>
                {sortBy === 'name' ? (
                  <>
                    {Object.keys(groupedFolders).sort().map((letter, index) => (
                      <div key={`letter_${index}`} className="col-12">
                        <h6 className="date-heading">{letter}</h6>
                        <div className="row">
                          {groupedFolders[letter].map((group, idx) => (
                            <div key={`${letter}_${idx}`} className="col-12 col-md-6 col-lg-4 mb-3">
                              <FolderListItem user={user} userData={userData} group={group} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    {groupedFolders.today.length > 0 && (
                      <div className="col-12">
                        <h6 className="date-heading"><FormattedMessage {...MESSAGES.folderListToday} /></h6>
                        <div className="row">
                          {groupedFolders.today.map((group, index) => (
                            <div key={`today_${index}`} className="col-12 col-md-6 col-lg-4 mb-3">
                              <FolderListItem user={user} userData={userData} group={group} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {groupedFolders.last7Days.length > 0 && (
                      <div className="col-12">
                        <h6 className="date-heading"><FormattedMessage {...MESSAGES.folderList7Days} /></h6>
                        <div className="row">
                          {groupedFolders.last7Days.map((group, index) => (
                            <div key={`last7days_${index}`} className="col-12 col-md-6 col-lg-4 mb-3">
                              <FolderListItem user={user} userData={userData} group={group} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {groupedFolders.last30Days.length > 0 && (
                      <div className="col-12">
                        <h6 className="date-heading"><FormattedMessage {...MESSAGES.folderList30Days} /></h6>
                        <div className="row">
                          {groupedFolders.last30Days.map((group, index) => (
                            <div key={`last30days_${index}`} className="col-12 col-md-6 col-lg-4 mb-3">
                              <FolderListItem user={user} userData={userData} group={group} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {Object.keys(groupedFolders.older).map((key, index) => {
                      const { monthYear, folders } = groupedFolders.older[key];

                      return (
                        <div key={`older_${index}`} className="col-12">
                          <h6 className="date-heading">
                            <FormattedDate
                              value={new Date(`${monthYear.year}-${monthYear.month}-01`)}
                              year="numeric"
                              month="long"
                            />
                          </h6>
                          <div className="row">
                            {folders.map((group: GroupModel, idx: number) => (
                              <div key={`${key}_${idx}`} className="col-12 col-md-6 col-lg-4 mb-3">
                                <FolderListItem user={user} userData={userData} group={group} />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </>
						) || (
							<div className="folder-none col-12">
								<FormattedMessage {...MESSAGES.folderListNone} />
							</div>
						)}
          </div>
        </div>
      </div>
    );
  }

  private get groupsForCurrentTab(): GroupModel[] | undefined {
    const { groups, authenticator, params } = this.props;
    if (!groups || !groups.result || !authenticator.accessToken) {
      return [];
    }

    let groupsForTab = groups.result;
    if (this.props.folderId) {
      groupsForTab = groupsForTab.filter(g => g.parentId === this.props.folderId);
    } else {
      groupsForTab = groupsForTab.filter(g => !g.parentId);
    }

    return groupsForTab;
  }
}

export const FolderListHeadered = connect((state: GlobalPrivateState) => ({
  groups: state.groups,
  authenticator: state.authenticator,
  user: state.user,
  userData: state.userData,
  location: state.router.location,
  params: state.queryState.params,
  queryState: state.queryState,
}))(FolderListHeaderedPartial);

export default FolderListHeadered;